// Stage 2 "Dashboard" · 2.3 (b) — the Portfolio table: fixed widths (20/10/10/10/11/14/13/12 — SUNDAY BATCH D-1), every cell a link that selects the
// engagement and opens its page; a 4 px edge on the start side of each row in the opinion colour.
import React from "react";
import { fmtDate } from "@/lib/dt";
import { fmtNumber } from "@/lib/format";
import { FD, STAGE_CHIP, OPINION_CHIP, currencyBadge, TARGET, fill } from "./fdTheme";
import { FdLink, Panel, Td, Chip, Empty } from "./FdPrimitives";
import { FdResizableTable, FdRTh } from "./FdResizable";   // Step 11d · add-on: adjustable columns
import { useResizableColumns } from "@/components/ui/resizable-table";
import NumPair from "@/components/NumPair";
import UpdatingNote, { isMissing } from "./StoredUpdating";   // D-10: stored figures — "Updating…" next to the last value

const pick = (row, en, ar, lang) => (lang === "ar" && row[ar]) || row[en] || "";

export default function FdPortfolio({ rows, t, lang }) {
  const n = (v, o) => fmtNumber(v || 0, lang, o);
  const list = Array.isArray(rows) ? rows : [];
  // Step 11d · add-on: client, status, tested, sign-offs, lock, report, PM, review points (were 20/10/10/10/11/14/13/12 %)
  const cols = useResizableColumns({ storageKey: "dashboard-portfolio", defaults: [230, 115, 115, 115, 126, 161, 150, 138],
                                     mins: [150, 90, 90, 90, 90, 110, 100, 100] });
  return (
    <Panel title={t("fd.portfolio.title")} testid="fd-portfolio">
      <FdResizableTable cols={cols} testid="fd-portfolio-table">
        <thead>
          <tr>
            <FdRTh index={0}>{t("fd.portfolio.client")}</FdRTh><FdRTh index={1}>{t("fd.portfolio.status")}</FdRTh><FdRTh index={2}>{t("fd.portfolio.tested")}</FdRTh>
            <FdRTh index={3}>{t("fd.portfolio.signoffs")}</FdRTh><FdRTh index={4}>{t("fd.portfolio.lock")}</FdRTh><FdRTh index={5}>{t("fd.portfolio.report")}</FdRTh>
            <FdRTh index={6}>{t("fd.portfolio.pm")}</FdRTh>
            <FdRTh index={7}>{t("fd.portfolio.rp")}<span className="block text-[10px] font-normal normal-case tracking-normal" style={{ color: FD.muted }}>{t("fd.portfolio.rpHint")}</span></FdRTh>
          </tr>
        </thead>
        <tbody>
          {list.length === 0 && <tr><Td colSpan={8}><Empty>{t("fd.portfolio.empty")}</Empty></Td></tr>}
          {list.map((r) => {
            const id = r.engagement_id;
            if (isMissing(r.stored)) {          // D-10: never calculated yet — "Updating…", never 0 / blank cells
              return (
                <tr key={id} data-testid={`fd-pf-row-${id}`}>
                  <Td testid={`fd-pf-cell-client-${id}`}>
                    <FdLink to={TARGET.engagements} engId={id} testid={`fd-pf-client-${id}`} className="font-medium">{r.client_name}</FdLink>
                  </Td>
                  <Td colSpan={7}><UpdatingNote stored={r.stored} t={t} testid={`fd-pf-updating-${id}`} /></Td>
                </tr>
              );
            }
            const st = r.status || {}; const rp = r.report || {}; const te = r.tested || {}; const so = r.signoffs || {};
            const lk = r.lock || {}; const ms = r.misstatements || {}; const rv = r.review_points || {};
            const stage = st.stage || "planning";
            const sc = STAGE_CHIP[stage] || STAGE_CHIP.planning;
            const oc = OPINION_CHIP[rp.opinion] || OPINION_CHIP.not_concluded;
            const cb = currencyBadge(r.currency);
            const testedTone = Number(te.tested || 0) === Number(te.selected || 0) ? "green" : "amber";
            const pmTone = ms.performance_materiality ? (ms.at_or_over_pm ? "red" : "ok") : "none";
            const toneColor = { green: FD.green, amber: FD.amber, red: FD.red, ok: FD.text, none: FD.muted };
            return (
              <tr key={id} data-testid={`fd-pf-row-${id}`} style={{ borderInlineStart: `4px solid ${oc.fg}` }}>
                <Td testid={`fd-pf-cell-client-${id}`} style={{ borderInlineStart: `4px solid ${oc.fg}` }}>
                  <FdLink to={TARGET.engagements} engId={id} testid={`fd-pf-client-${id}`} className="font-medium">{r.client_name}</FdLink>
                  <Chip bg={cb.bg} fg={cb.fg} testid={`fd-cur-${id}`} className="mt-1">{r.currency || "—"}</Chip>
                  <UpdatingNote stored={r.stored} t={t} testid={`fd-pf-updating-${id}`} className="mt-1" />
                </Td>
                <Td>
                  <FdLink to={TARGET.stagePage(stage)} engId={id} testid={`fd-pf-status-${id}`}>
                    <Chip bg={sc.bg} fg={sc.fg} testid={`fd-chip-stage-${id}`}>{pick(st, "stage_label", "stage_label_ar", lang) || t(`fd.stage.${stage}`)}</Chip>
                  </FdLink>
                </Td>
                <Td>
                  <FdLink to={TARGET.vouching} engId={id} className="font-semibold tabular-nums" style={{ color: toneColor[testedTone] }}>
                    <span data-tone={testedTone} data-testid={`fd-pf-tested-${id}`}><NumPair parts={[n(te.tested), n(te.selected)]} /></span>
                  </FdLink>
                </Td>
                <Td>
                  <FdLink to={TARGET.signoffs} engId={id} testid={`fd-pf-signoffs-${id}`} className="tabular-nums"><NumPair parts={[n(so.signed), n(so.total)]} /></FdLink>
                </Td>
                <Td>
                  <FdLink to={TARGET.fileAssembly} engId={id} testid={`fd-pf-lock-${id}`} style={{ color: lk.ready ? FD.green : FD.text }}>
                    {lk.ready ? t("fd.portfolio.ready") : fill(t("fd.portfolio.openItems"), { n: n(lk.open_items) })}
                    {Number(lk.warnings || 0) > 0 ? <span className="block text-xs" style={{ color: FD.amber }}>{fill(t("fd.portfolio.warnings"), { n: n(lk.warnings) })}</span> : null}
                  </FdLink>
                </Td>
                <Td>
                  <FdLink to={TARGET.auditorsReport} engId={id} testid={`fd-pf-report-${id}`}>
                    <Chip bg={oc.bg} fg={oc.fg} testid={`fd-chip-opinion-${id}`}>{pick(rp, "opinion_label", "opinion_label_ar", lang) || t(`fd.opinion.${rp.opinion || "not_concluded"}`)}</Chip>
                    <span className="block text-xs" style={{ color: FD.muted }}>{rp.report_date ? fmtDate(rp.report_date) : t("fd.portfolio.notDated")}</span>
                  </FdLink>
                </Td>
                <Td>
                  <FdLink to={TARGET.w9} engId={id} className="tabular-nums" style={{ color: toneColor[pmTone] }}>
                    <span data-tone={pmTone} data-testid={`fd-pf-pm-${id}`}>
                      {ms.performance_materiality
                        ? <><NumPair parts={[n(ms.aggregate_compared), n(ms.performance_materiality)]} /><span className="block text-xs">{fill(t("fd.ms.ofPm"), { pct: n(ms.pct, { maximumFractionDigits: 0 }) })}</span></>
                        : t("fd.portfolio.noPm")}
                    </span>
                  </FdLink>
                </Td>
                <Td align="end">
                  {/* D-1: total / resolved / unresolved (resolved = cleared by the raiser; the API's one rule) */}
                  <FdLink to={TARGET.reviewPoints} engId={id} testid={`fd-pf-rp-${id}`} className="font-semibold tabular-nums" style={{ color: Number(rv.open || 0) > 0 ? FD.amber : FD.text }}>
                    {typeof rv.total === "number" ? <NumPair parts={[n(rv.total), n(rv.resolved), n(rv.unresolved)]} /> : n(rv.open)}
                  </FdLink>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </FdResizableTable>
    </Panel>
  );
}
